import {
  Heading,
  Text,
  Button,
  Link,
  Carousel,
  Icon,
  Modal,
  Spinner,
  Tag,
} from "@ttc3k/trekker";
import { ArrowLeft, Dialpad, SelectWindow, SendDiagonal } from "iconoir-react";
import { Box, Center, Flex } from "styled-system/jsx";
import { css } from "styled-system/css";
import { HoursOfOperation } from "./HoursOfOperation";
import { KioskQRCode } from "./KioskQRCode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createGoogleMapsUrl,
  getAddressString,
  getBlurhashDataUrl,
  getFormattedDetailsLink,
  getMultilingualString,
} from "shared/utils";
import _ from "lodash";
import { CloudinaryImage, StaticMap, WhereaboutsLogo } from "shared/components";
import { SmartImage } from "shared/components/atoms";
import { AssetImage, EnumOperatorOperationalStatus } from "gql/generated";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import { WidgetOperatorsWithOverrides } from "widgets/operators/shared/types";
import { MultilingualLangKey } from "shared/types";
import { isLegacyOperatorsWidget } from "widgets/shared/isLegacyOperatorsWidget";
import { useOperatorPromotions } from "widgets/shared/WidgetPromotionsContext";
import {
  PromotionSlideout,
  type PromotionDetailsData,
} from "shared/components/organisms/PromotionDetails";
import { PromotionsSection } from "./PromotionsSection";
import { addMonths, format } from "date-fns";
import { es, fr } from "date-fns/locale";
import { useEventManyQuery } from "gql/generated";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { useEventDetailsDataMerger } from "widgets/events/components/EventsContext/useEventDetailsDataMerger";
import { DetailsSidePane as EventDetailsSidePane } from "shared/components/organisms/EventDetailsSidePane/DetailsSidePane";

/**
 * "Back" trigger used when the listing slideout was opened from another
 * slideout (Promotions widget): a solid dark 32px circle 24px from the
 * top-right corner, over the gallery, matching the promotion slideout's close
 * button so going back lands on the same spot.
 */
const backTriggerClassName = css({
  bgColor: "ink.1000",
  borderRadius: "full",
  w: "32px",
  h: "32px",
  p: "0",
  top: "24px",
  right: "24px",
  left: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow:
    "0 4px 10px rgba(197, 189, 181, 0.3), 0 2.6px 6px rgba(197, 189, 181, 0.22), 0 2px 2.7px rgba(197, 189, 181, 0.14), 0 0.7px 1px rgba(197, 189, 181, 0.06)",
  _hover: { bgColor: "ink.900" },
  _focusVisible: { bgColor: "ink.900" },
});

export type DetailsSidePaneProps = {
  selectedOperatorId: string | undefined;
  lang?: MultilingualLangKey;
  operatorDetailsLink?: string;
  operators: WidgetOperatorsWithOverrides;
  organizationId: string;
  widgetId?: string;
  kioskMode?: boolean;
  kioskQrLink?: string;
  /** Per-widget setting: list the listing's upcoming approved events. */
  showEvents?: boolean;
  /**
   * Replace the close button with a "Back" button: for a pane stacked on top
   * of another slideout, which stays open underneath and is revealed again
   * when this one slides out.
   */
  backButton?: boolean;
  slideoutOverrides?: {
    limitImages?: number | null;
    hideAddress?: boolean | null;
    hideLongDescription?: boolean | null;
    hideExternalLinks?: boolean | null;
    hideOperatingHours?: boolean | null;
    hideMap?: boolean | null;
  };
  onExitComplete?: () => void;
};

export const DetailsSidePane = ({
  selectedOperatorId,
  lang,
  operatorDetailsLink,
  operators,
  widgetId,
  slideoutOverrides,
  organizationId,
  kioskMode = false,
  kioskQrLink,
  showEvents = false,
  backButton = false,
  onExitComplete,
}: DetailsSidePaneProps) => {
  const { t } = useTranslation();
  const trackPostHogEvent = useWidgetTrack();
  const operator = operators.find(op => op._entityId === selectedOperatorId);
  const coordinates: [number, number] = [
    operator?.contact.address?.location?.coordinates[0] ?? 0,
    operator?.contact.address?.location?.coordinates[1] ?? 0,
  ];
  const isValidCoordinates = coordinates[0] !== 0 && coordinates[1] !== 0;
  const isValidHoursOfOperation =
    operator &&
    !_.isEmpty(operator.hoursOfOperation) &&
    Object.keys(operator.hoursOfOperation).length > 0;

  const shortDescription = operator?.overrides?.shortDescription
    ? getMultilingualString(operator.overrides.shortDescription, lang)
    : getMultilingualString(operator?.shortDescription ?? {}, lang);
  const description = operator?.overrides?.description
    ? getMultilingualString(operator.overrides.description, lang)
    : getMultilingualString(operator?.description ?? {}, lang);

  const isShortDescriptionSimilarToDescription = useMemo(
    () =>
      description
        ?.toLowerCase()
        .replace(/[^0-9A-Za-z]/g, "")
        .includes(
          shortDescription
            ?.toLowerCase()
            .replace(/[^0-9A-Za-z]/g, "")
            .substring(0, 25),
        ),
    [description, shortDescription],
  );

  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(!!selectedOperatorId);
  }, [selectedOperatorId]);

  // Promotions: listed before the description so deals show without
  // scrolling past long copy; "Info" stacks a promotion slideout on top of
  // this one, and "Learn more about <listing>" closes it.
  const promotions = useOperatorPromotions(operator?._entityId);
  const [selectedPromotionId, setSelectedPromotionId] = useState<
    string | undefined
  >(undefined);
  useEffect(() => {
    setSelectedPromotionId(undefined);
  }, [selectedOperatorId]);
  const selectedPromotion = useMemo<PromotionDetailsData | null>(() => {
    const promotion = promotions.find(p => p._id === selectedPromotionId);
    if (!promotion) return null;
    return {
      title: getMultilingualString(promotion.title, lang),
      description: getMultilingualString(promotion.description, lang),
      link: promotion.link,
      availability: promotion.availability,
      images: promotion.images.filter(
        (image): image is NonNullable<typeof image> => !!image,
      ),
      listingName: operator ? getMultilingualString(operator.name, lang) : "",
    };
  }, [promotions, selectedPromotionId, lang, operator]);

  // Upcoming events (12 months), only when the widget shows them. The org's
  // event widget settings decide whether cancelled events are included.
  const eventDateRange = useMemo(
    () => ({
      startDate: format(new Date(), EVENT_DATE_FORMAT),
      endDate: format(addMonths(new Date(), 12), EVENT_DATE_FORMAT),
    }),
    [],
  );
  const { data: eventsData } = useEventManyQuery({
    variables: {
      ...eventDateRange,
      organizationId,
      operatorId: operator?._entityId ?? "",
      useOrgEventWidgetSettings: true,
    },
    skip: !showEvents || !operator?._entityId,
  });
  const events = useMemo(
    () =>
      showEvents
        ? _.sortBy(eventsData?.eventMany ?? [], e => e.occurrences?.[0])
        : [],
    [eventsData, showEvents],
  );
  const { selectedEvent, setSelectedEventId } =
    useEventDetailsDataMerger(events);
  useEffect(() => {
    setSelectedEventId(null);
  }, [selectedOperatorId, setSelectedEventId]);
  const dateLocale = lang === "fr" ? fr : lang === "es" ? es : undefined;

  /**
   * When the screen is < 768px in width, the image is full width
   * - In these cases, we need to account for a possible scrollbar (e.g. mobile vs desktop)
   *   and use the scrollWidth of the main flex contianer as the image width to fetch
   *   from Cloudinary
   * - Unfortunately, that main flex container isn't drawn until the operator details are
   *   found, so need to run this code in a timeout
   */
  const [imageWidth, setImageWidth] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  // Recursive function to wait for the UI to draw
  const checkScrollWidth = useCallback(() => {
    if (contentRef.current) {
      setImageWidth(contentRef.current.scrollWidth);
    } else {
      setTimeout(() => checkScrollWidth(), 1);
    }
  }, [setImageWidth]);
  // Start the size checking when the operator changes
  useEffect(() => {
    setImageWidth(0);
    setTimeout(() => checkScrollWidth(), 1);
  }, [checkScrollWidth, operator]);

  // Set the image array for the carousel
  const overrideImages = _.get(operator, "overrides.images", []);
  const images =
    overrideImages.length > 0 ? overrideImages : _.get(operator, "images", []);
  const hideGallery = slideoutOverrides?.limitImages === 0;
  const limitedImages =
    typeof slideoutOverrides?.limitImages === "number" &&
    slideoutOverrides.limitImages > 0
      ? images.slice(0, slideoutOverrides.limitImages)
      : images;

  // Open the operator details link in a new tab/window
  const handleOperatorDetailsLinkClick = () => {
    trackPostHogEvent(PostHogCustomEvent.ExpandedDetailsClick, {
      organizationId: organizationId,
      operatorEntityId: operator?._entityId,
      widgetId,
    });
    window.open(
      getFormattedDetailsLink(
        getMultilingualString(operator?.name ?? {}, lang),
        operatorDetailsLink!,
        operator?._entityId ?? "",
      ),
      "_blank",
      "noopener",
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={({ open }: { open: boolean }) => setOpen(open)}
      closeTriggerVisual={backButton ? "subtle" : "light"}
      {...(backButton
        ? {
            closeTriggerPlacement: "whereabouts" as const,
            closeTriggerProps: {
              // Static css() so Panda emits these styles; the offsets are
              // inline because the close-trigger recipe sets its own.
              className: backTriggerClassName,
              style: { top: 24, right: 24 },
              "aria-label": t("core:BACK"),
              part: "details__back",
              children: (
                <Icon
                  Element={ArrowLeft}
                  w="20px"
                  h="20px"
                  color="icon.inverse"
                />
              ),
            },
          }
        : {})}
      onExitComplete={onExitComplete}
      zIndex={"max"}
      portal={!isLegacyOperatorsWidget}
      {...(isLegacyOperatorsWidget
        ? {
            lazyMount: false,
            dialogContentPositioningProps: {
              width: 465,
              maxWidth: "100vw",
              height: "100vh",
              maxHeight: "100vh",
              marginLeft: "auto",
            },
            backdropProps: {
              style: { backgroundColor: "rgba(10, 10, 10, 0.45)" },
            },
          }
        : {})}
    >
      {!operator ? (
        <Heading mx={400} my={600}>
          {t("widgets:OPERATOR.ERROR.FETCH")}
        </Heading>
      ) : (
        <Flex flexDir={"column"} h={"100%"} ref={contentRef}>
          {!hideGallery && (
            <Box position="sticky" top={0} h="400px" part="details__gallery">
              {limitedImages.length > 0 && imageWidth > 0 ? (
                <Carousel
                  images={limitedImages}
                  controlClassName={css({ pb: "0" })}
                  tagClassName={css({ pb: "0" })}
                  render={(image: Partial<AssetImage>) => (
                    <Flex
                      alignItems={"center"}
                      justifyContent={"center"}
                      h="400px"
                      style={{
                        backgroundImage: getBlurhashDataUrl(image.blurhash),
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <SmartImage
                        image={image}
                        width={imageWidth || 465}
                        height={400}
                        shape="square"
                        previewVideo={true}
                      />
                    </Flex>
                  )}
                />
              ) : limitedImages.length === 0 ? (
                <CloudinaryImage
                  imageStyleProps={{
                    h: "400px",
                    borderStyle: "none",
                    borderColor: "transparent",
                    borderRadius: "0",
                    aspectRatio: `${imageWidth} : 400`,
                    width: "full",
                  }}
                />
              ) : (
                <Center h="400px" w="full">
                  <Spinner width={"40px"} height={"40px"} />
                </Center>
              )}
            </Box>
          )}
          <Flex
            part={"details__main"}
            flexDir={"column"}
            flexGrow={1}
            p="600"
            bg={"bg.lightest"}
            zIndex={1}
            gap="300"
          >
            <Flex
              flexDir="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="400"
              mb="150"
            >
              <Flex flexDir="column" gap="100">
                <Heading size={"h3"} part="details__name">
                  {getMultilingualString(operator.name, lang)}
                </Heading>
                {(operator.operationalStatus ===
                  EnumOperatorOperationalStatus.PermanentlyClosed ||
                  operator.operationalStatus ===
                    EnumOperatorOperationalStatus.TemporarilyClosed) && (
                  <Tag
                    visual={
                      operator.operationalStatus ===
                      EnumOperatorOperationalStatus.PermanentlyClosed
                        ? "destructive"
                        : "warning"
                    }
                    mode="light"
                    size="sm"
                    part="details__status-tag"
                  >
                    {t(
                      `core:LOOKUPS.OPERATOR_OPERATIONAL_STATUS.${operator.operationalStatus}`,
                    )}
                  </Tag>
                )}
              </Flex>
              {operatorDetailsLink && !kioskMode && (
                <Button
                  part={"details__btn-see-more"}
                  visual="primary"
                  flexShrink={0}
                  py="9px"
                  onClick={handleOperatorDetailsLinkClick}
                >
                  {t("widgets:OPERATOR.DETAILS_SIDE_PANE.SEE_MORE")}
                </Button>
              )}
            </Flex>

            {/* Link to Google Maps + Buttons for website / phone / email */}
            <Flex flexDir="column" gap="200" part="details__contacts">
              {!slideoutOverrides?.hideAddress && operator.contact.address && (
                <Flex flexDir="column" gap="50">
                  <Text part={"details__address-title"} visual="smallSemiBold">
                    {t("widgets:OPERATOR.DETAILS_SIDE_PANE.ADDRESS.TITLE")}
                  </Text>
                  {kioskMode ? (
                    <Text
                      part={"details__address-text"}
                      visual="smallRegular"
                      color="text.mid"
                    >
                      {getAddressString(operator.contact.address)}
                    </Text>
                  ) : (
                    <Link
                      part={"details__address-link"}
                      justifyContent="flex-start"
                      href={createGoogleMapsUrl(operator.contact.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      color={{
                        base: "text.mid",
                        hover: "text.dark",
                        focusVisible: "text.dark",
                      }}
                      textDecoration="underline"
                    >
                      {getAddressString(operator.contact.address)}
                    </Link>
                  )}
                </Flex>
              )}
              {kioskMode
                ? (() => {
                    const qrUrl = kioskQrLink
                      ? getFormattedDetailsLink(
                          getMultilingualString(operator.name ?? {}, lang),
                          kioskQrLink,
                          operator._entityId,
                        )
                      : operator.contact.links?.website
                        ? operator.contact.links.website
                        : operator.contact.address
                          ? createGoogleMapsUrl(operator.contact.address)
                          : null;
                    return qrUrl ? <KioskQRCode url={qrUrl} /> : null;
                  })()
                : !slideoutOverrides?.hideExternalLinks &&
                  (operator.contact.links?.website ||
                    operator.contact.phone ||
                    operator.contact.email) && (
                    <Flex flexDir="row" gap={"200"}>
                      {operator.contact.links?.website && (
                        <Button
                          part={"details__btn-contact"}
                          mode={"light"}
                          onClick={() => {
                            if (operator.contact.links?.website) {
                              trackPostHogEvent(
                                PostHogCustomEvent.OutboundClick,
                                {
                                  organizationId: organizationId,
                                  operatorEntityId: operator?._entityId,
                                  widgetId: widgetId,
                                },
                              );
                              window.open(
                                operator.contact.links.website,
                                "_blank",
                                "noopener",
                              );
                            }
                          }}
                          Icon={SelectWindow}
                        >
                          {t("widgets:OPERATOR.DETAILS_SIDE_PANE.WEBSITE")}
                        </Button>
                      )}
                      {operator.contact.phone && (
                        <Button
                          part={"details__btn-contact"}
                          mode={"light"}
                          onClick={() =>
                            (window.location.href = `tel:${operator.contact.phone}`)
                          }
                          Icon={Dialpad}
                        >
                          {t("widgets:OPERATOR.DETAILS_SIDE_PANE.PHONE")}
                        </Button>
                      )}
                      {operator.contact.email && (
                        <Button
                          part={"details__btn-contact"}
                          mode={"light"}
                          onClick={() =>
                            (window.location.href = `mailto:${operator.contact.email}`)
                          }
                          Icon={SendDiagonal}
                        >
                          {t("widgets:OPERATOR.DETAILS_SIDE_PANE.EMAIL")}
                        </Button>
                      )}
                    </Flex>
                  )}
            </Flex>

            <PromotionsSection
              promotions={promotions}
              events={events}
              lang={lang}
              onPromotionClick={setSelectedPromotionId}
              onEventClick={setSelectedEventId}
            />

            {!slideoutOverrides?.hideLongDescription &&
              (shortDescription || description) && (
                <Flex flexDir="column" gap="150">
                  {shortDescription &&
                    !isShortDescriptionSimilarToDescription && (
                      <Text
                        part={"details__short-description"}
                        visual="bodySemiBold"
                        whiteSpace="pre-wrap"
                      >
                        {shortDescription}
                      </Text>
                    )}
                  {description && (
                    <Text
                      part={"details__description"}
                      visual={"bodyRegular"}
                      color={"text.mid"}
                      whiteSpace="pre-wrap"
                    >
                      {description}
                    </Text>
                  )}
                </Flex>
              )}

            {!slideoutOverrides?.hideOperatingHours &&
              isValidHoursOfOperation && (
                <Flex flexDir="column" gap="50">
                  <Text
                    part={"details__operating-hours-title"}
                    visual={"bodySemiBold"}
                  >
                    {t(
                      "widgets:OPERATOR.DETAILS_SIDE_PANE.OPERATING_HOURS.TITLE",
                    )}
                  </Text>
                  <HoursOfOperation data={operator.hoursOfOperation} />
                </Flex>
              )}

            {!slideoutOverrides?.hideMap && isValidCoordinates && (
              <StaticMap
                locations={[
                  {
                    latitude: coordinates[1],
                    longitude: coordinates[0],
                  },
                ]}
                style={{
                  minHeight: "190px",
                  transition: "height 0.3s ease",
                }}
                disableClick={kioskMode}
              />
            )}

            {kioskMode ? (
              <Flex
                display="flex"
                flexDir="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                gap="75"
                userSelect="none"
                color="text.dark"
                part="details__footer"
                aria-label={`${t("widgets:POWERED_BY")} Whereabouts`}
              >
                <Text visual={"smallTag"} opacity={0.5}>
                  {t("widgets:POWERED_BY")}
                </Text>
                <WhereaboutsLogo width={100} height={"auto"} />
              </Flex>
            ) : (
              <Link
                href={"https://www.whereabouts.tech/"}
                display={"flex"}
                flexDir="column"
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
                gap="75"
                userSelect={"none"}
                rel="noopener noreferrer"
                target="_blank"
                color={"text.dark"}
                part={"details__footer"}
                aria-label={`${t("widgets:POWERED_BY")} Whereabouts`}
              >
                <Text visual={"smallTag"} opacity={0.5}>
                  {t("widgets:POWERED_BY")}
                </Text>
                <WhereaboutsLogo width={100} height={"auto"} />
              </Link>
            )}
          </Flex>
          <PromotionSlideout
            isOpen={!!selectedPromotionId}
            onClose={() => setSelectedPromotionId(undefined)}
            promotion={selectedPromotion}
            onListingClick={() => setSelectedPromotionId(undefined)}
            portal={!isLegacyOperatorsWidget}
            part="details__promotion"
          />
          {showEvents && (
            <EventDetailsSidePane
              lang={lang}
              selectedEvent={selectedEvent}
              setSelectedEvent={() => setSelectedEventId(null)}
              locale={dateLocale}
              organizationId={organizationId}
              widgetId={widgetId}
              portal={!isLegacyOperatorsWidget}
            />
          )}
        </Flex>
      )}
    </Modal>
  );
};
